import { type ReactNode, useEffect, useState } from 'react'
import {
  resolveSessionStatus,
  type SessionStatus,
} from '@/core/auth/ensure-session'
import { clearMarkerCookie } from '@/core/auth/marker-cookie'
import { useAuthStore } from '@/features/auth/store'
import { PageSpinner } from '@/shared/components/layout/suspense-wrappers'
import { appConfig } from '@/shared/config/app-config'
import { buildExternalUrl } from '@/shared/lib/external-url'

type GuardStatus = SessionStatus | 'checking'

export function ProtectedRoute({
  children,
  fallback,
}: {
  children: ReactNode
  // When set, an invalid/lost session renders this instead of the external
  // /login redirect. The cancel flows opt in: native-app users reach those
  // pages via autologin inside a webView (no password), so bouncing a mid-flow
  // 401 / expired session out to /login is a dead end — show an in-app error.
  fallback?: ReactNode
}) {
  const [status, setStatus] = useState<GuardStatus>('checking')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

  // Validate (and refresh if needed) the session before mounting children.
  // `isAuthenticated` alone only means "a session blob exists" — a stale or
  // expired one would still mount the page and fire token-less /v2 + /graphql
  // requests that 401. Gating on a real token kills that flood at the source.
  useEffect(() => {
    let cancelled = false
    resolveSessionStatus().then((resolved) => {
      if (!cancelled) setStatus(resolved)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (status === 'checking') {
    return <PageSpinner />
  }

  // Redirect when the initial check failed, or when a later logout /
  // mid-session invalidation flips isAuthenticated to false.
  if (status === 'unauthed' || !isAuthenticated) {
    clearMarkerCookie()
    // A route that provided a fallback (the cancel flows) shows it in-app rather
    // than dead-ending a passwordless webView user at the external /login page.
    if (fallback !== undefined) {
      return <>{fallback}</>
    }
    window.location.href = buildExternalUrl(`${appConfig.siteUrl}/en/login`)
    return null
  }

  return <>{children}</>
}
